import { NestAPI } from '@marketing-milk/frontend'
import { AdCreative, AdCreativeReview, AdCreativeReviewDTO } from '@marketing-milk/interfaces'
import { useMutation, useQueryClient } from 'react-query'
import { commentsQueryKey } from './useComments'

interface ClientApprovalInput {
  clientApproverName: string
  notes?: string
}

/**
 * Records a client (external) approval or rejection on a creative through the
 * shared reviews controller — either AE-attested (`approveOnBehalfOfClient`)
 * or the client's own direct decision (`submitReview`). On success it
 * invalidates every creative consumer so the status badge flips in the
 * packet, the creatives list, CreativeWizard, and campaign-builder eligibility.
 */
export const useAdCreativeReview = (businessID: number, creativeID: number) => {
  const queryClient = useQueryClient()

  // Prefix-invalidate so sprint creatives, the creatives list, CreativeWizard,
  // and campaign-builder creative sources all refetch the new review status.
  // Also refresh the creative's comment thread, since approve/remove now post an
  // attested-approval event comment there.
  //
  // Scoped to `['sprints', 'sprint-creatives']` (matching `useSprint`'s
  // `sprintCreatives` query key), NOT the bare `['sprints']` prefix — that
  // also matches the separate sprints-*list* query, which a client-review
  // user (no `AccessAdBuilderApp`) can't read. Invalidating it 401s and
  // retries for several seconds, stalling this promise (and anything
  // awaiting `mutateAsync`, e.g. auto-closing the review modal) for no
  // reason, since that query isn't even used on this page.
  const invalidateCreativeConsumers = async () => {
    await Promise.all([
      queryClient.invalidateQueries(['sprints', 'sprint-creatives']),
      queryClient.invalidateQueries(['creatives']),
      queryClient.invalidateQueries(['creatives-by-ids']),
      queryClient.invalidateQueries(commentsQueryKey(businessID, 'creative', String(creativeID))),
    ])
  }

  const approveOnBehalfOfClient = useMutation(
    async (input: ClientApprovalInput): Promise<AdCreativeReview> => {
      const dto: AdCreativeReviewDTO = {
        approved: true,
        onBehalfOfClient: true,
        clientApproverName: input.clientApproverName,
        notes: input.notes,
      }
      const { data } = await NestAPI.post<AdCreativeReview>(
        `businesses/${businessID}/ad-creatives/${creativeID}/reviews`,
        dto
      )
      return data
    },
    { onSuccess: invalidateCreativeConsumers }
  )

  const removeApproval = useMutation(
    async (): Promise<AdCreative> => {
      const { data } = await NestAPI.delete<AdCreative>(
        `businesses/${businessID}/ad-creatives/${creativeID}/reviews`
      )
      return data
    },
    { onSuccess: invalidateCreativeConsumers }
  )

  // The client review page only ever renders `sprintCreatives` (via
  // `useSprint`) and doesn't read the general creatives list, CreativeWizard,
  // or campaign-builder sources — so `submitReview` gets its own, narrower
  // invalidation instead of reusing `invalidateCreativeConsumers`. That
  // matters beyond just avoiding pointless work: `useSprint` unconditionally
  // pulls in `useCreatives` (for `duplicateManyCreatives`), which keeps an
  // AE-only `GET .../ad-creatives` list query active even on this page.
  // Invalidating `['creatives']` there 401s for a client account and retries
  // for several seconds, stalling `mutateAsync` (react-query v3 awaits
  // `onSuccess` before settling `isLoading`) — and with it, this modal's
  // auto-close.
  const invalidateReviewConsumers = async () => {
    await Promise.all([
      queryClient.invalidateQueries(['sprints', 'sprint-creatives']),
      queryClient.invalidateQueries(commentsQueryKey(businessID, 'creative', String(creativeID))),
    ])
  }

  // A client's own direct approve/reject — no `onBehalfOfClient`/approver name,
  // the logged-in user IS the reviewer. `notes` carries the rejection reason.
  const submitReview = useMutation(
    async (input: { approved: boolean; notes?: string }): Promise<AdCreativeReview> => {
      const dto: AdCreativeReviewDTO = { approved: input.approved, notes: input.notes }
      const { data } = await NestAPI.post<AdCreativeReview>(
        `businesses/${businessID}/ad-creatives/${creativeID}/reviews`,
        dto
      )
      return data
    },
    { onSuccess: invalidateReviewConsumers }
  )

  return { approveOnBehalfOfClient, removeApproval, submitReview }
}
